import type {
  DnsRecordMxCustomData,
  DnsRecordMxData,
  DnsRecordMxEntry,
  DnsRecordMxManagedData,
  DnsRecordMxUnsetData,
} from "./types.js";
import { DataModel } from "../../base/index.js";
import {
  DnsRecordSettings,
  type DnsRecordSettingsData,
} from "../DnsRecordSettings/index.js";
import type { DnsZoneCommon } from "../DnsZone/index.js";

abstract class DnsRecordMxBase<T extends DnsRecordMxData> extends DataModel<T> {
  public readonly dnsZone: DnsZoneCommon;
  public constructor(dnsZone: DnsZoneCommon, data: T) {
    super(data);
    this.dnsZone = dnsZone;
  }

  public async addEntry(
    entry: DnsRecordMxEntry,
    settings: DnsRecordSettingsData,
  ) {
    const existingItems =
      this.dnsZone.recordSet.mx.type === "custom"
        ? this.dnsZone.recordSet.mx.records
        : [];
    await this.dnsZone.setCustomMxRecord([entry, ...existingItems], settings);
  }

  public async removeEntry(entry: DnsRecordMxEntry) {
    const existingItems =
      this.dnsZone.recordSet.mx.type === "custom"
        ? this.dnsZone.recordSet.mx.records
        : [];
    if (
      existingItems.length <= 1 ||
      this.dnsZone.recordSet.mx.type !== "custom"
    ) {
      return;
    }
    const updated = existingItems.filter((i) => i !== entry);
    await this.dnsZone.setCustomMxRecord(
      updated,
      this.dnsZone.recordSet.mx.settings.data,
    );
  }

  public async updateEntry(
    entry: DnsRecordMxEntry,
    newEntry: DnsRecordMxEntry,
    settings: DnsRecordSettingsData,
  ) {
    const existingItems =
      this.dnsZone.recordSet.mx.type === "custom"
        ? this.dnsZone.recordSet.mx.records
        : [];
    if (
      existingItems.length < 1 ||
      this.dnsZone.recordSet.mx.type !== "custom"
    ) {
      return;
    }
    const updatedArray = this.dnsZone.recordSet.mx.records.map((i) =>
      i === entry ? newEntry : i,
    );
    await this.dnsZone.setCustomMxRecord(updatedArray, settings);
  }

  public async setManaged() {
    await this.dnsZone.setRecordManaged("mx");
  }
}

export class DnsRecordMxManaged extends DnsRecordMxBase<DnsRecordMxManagedData> {
  public readonly type = "managed";

  public constructor(dnsZone: DnsZoneCommon, data: DnsRecordMxManagedData) {
    super(dnsZone, data);
  }

  public getTtl(): number | "auto" {
    return "auto";
  }
}

export class DnsRecordMxCustom extends DnsRecordMxBase<DnsRecordMxCustomData> {
  public readonly type = "custom";
  public readonly records: DnsRecordMxEntry[];
  public readonly settings: DnsRecordSettings;
  public constructor(dnsZone: DnsZoneCommon, data: DnsRecordMxCustomData) {
    super(dnsZone, data);
    this.records = data.records;
    this.settings = new DnsRecordSettings(data.settings);
  }

  public getTtl(): number | "auto" {
    return this.settings.ttl;
  }
}

export class DnsRecordMxUnset extends DnsRecordMxBase<DnsRecordMxUnsetData> {
  public readonly type = "unset";
  public constructor(dnsZone: DnsZoneCommon, data: DnsRecordMxUnsetData) {
    super(dnsZone, data);
  }

  public getTtl(): number | "auto" {
    return "auto";
  }
}

export type DnsRecordMx =
  | DnsRecordMxManaged
  | DnsRecordMxCustom
  | DnsRecordMxUnset;

export const dnsRecordMxFactory = (
  dnsZone: DnsZoneCommon,
  data: DnsRecordMxData,
): DnsRecordMx => {
  if ("records" in data) {
    return new DnsRecordMxCustom(dnsZone, data);
  }
  if ("managed" in data) {
    return new DnsRecordMxManaged(dnsZone, data);
  }
  return new DnsRecordMxUnset(dnsZone, data);
};
